/**
 * admin-ops-api の audit_logs 横断ビュー用 fetch wrapper (issue #198 Phase 5)。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * GET /api/v1/audit-logs                       audit_logs 取得
 * GET /api/v1/audit-logs/self-access           actor_type=alys_owner 固定（自己アクセス履歴）
 *
 * 守るライン #1: server 側で必ず PII マスキング済の row が返ってくる。
 */

export interface AuditLogRow {
  id: string;
  workspaceId: string | null;
  actorType: string;
  actorId: string | null;
  actorRole: string | null;
  actionName: string;
  targetType: string;
  targetId: string | null;
  reason: string | null;
  beforeValue: unknown;
  afterValue: unknown;
  ipAddressHash: string | null;
  userAgent: string | null;
  occurredAt: string;
}

export interface AuditLogsResponse {
  rows: AuditLogRow[];
  fetched: number;
  limit: number;
  offset: number;
  configured: boolean;
}

export interface AuditLogsQuery {
  eventKind?: string;
  actorType?: string;
  targetType?: string;
  workspaceId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

function appendIfSet(params: URLSearchParams, key: string, value: string | number | undefined) {
  if (value === undefined || value === null) return;
  if (typeof value === 'number' && !Number.isFinite(value)) return;
  const s = String(value);
  if (s.trim() === '') return;
  params.append(key, s);
}

export function buildAuditLogsUrl(
  baseUrl: string,
  query: AuditLogsQuery,
  path = '/audit-logs',
): string {
  const params = new URLSearchParams();
  appendIfSet(params, 'event_kind', query.eventKind);
  appendIfSet(params, 'actor_type', query.actorType);
  appendIfSet(params, 'target_type', query.targetType);
  appendIfSet(params, 'workspace_id', query.workspaceId);
  appendIfSet(params, 'from', query.from);
  appendIfSet(params, 'to', query.to);
  appendIfSet(params, 'limit', query.limit);
  appendIfSet(params, 'offset', query.offset);
  const qs = params.toString();
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/api/v1${path}${qs ? `?${qs}` : ''}`;
}

export async function fetchAuditLogs(
  baseUrl: string,
  query: AuditLogsQuery,
): Promise<AuditLogsResponse> {
  const url = buildAuditLogsUrl(baseUrl, query, '/audit-logs');
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`admin-ops API /audit-logs failed: ${res.status}`);
  return (await res.json()) as AuditLogsResponse;
}

export async function fetchSelfAccessLogs(
  baseUrl: string,
  query: Omit<AuditLogsQuery, 'actorType'>,
): Promise<AuditLogsResponse> {
  const url = buildAuditLogsUrl(baseUrl, query, '/audit-logs/self-access');
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`admin-ops API /audit-logs/self-access failed: ${res.status}`);
  return (await res.json()) as AuditLogsResponse;
}
